import { useEffect, useState } from "react";
import { Database, Sparkles, AlertTriangle } from "lucide-react";
import { fetchDataSourceSummary } from "@/lib/api";

/**
 * Environment-aware data source badge.
 *
 * - PRODUCTION (real customer data)  → "NATIVE DB / LIVE DATA" (emerald)
 * - Preview/development              → "DB / SEED DATA" (slate)
 * - Missing data expected in payload → "MANGLER I RESPONSE" (amber)
 * - Derived/computed                 → "DERIVED" (blue)
 *
 * The environment is resolved server-side via GET /api/data-source-summary so
 * it can never leak "DB / SEED DATA" in production, even if the frontend is
 * cached.
 */
export default function DataSourceBadge({ entity, className = "" }) {
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        let mounted = true;
        fetchDataSourceSummary()
            .then((d) => { if (mounted) setSummary(d); })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    if (!summary) return null;

    const isProd = !!summary.is_production;
    const isMissing = (summary.missing_in_n8n || []).includes(entity);
    const src = summary[entity] || "db";

    if (isMissing) {
        return (
            <span data-testid={`source-badge-${entity}`} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-100 ${className}`}>
                <AlertTriangle className="w-3 h-3" />
                Mangler i integration
            </span>
        );
    }
    if (src === "derived") {
        return (
            <span data-testid={`source-badge-${entity}`} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-blue-50 text-blue-700 border-blue-100 ${className}`}>
                <Database className="w-3 h-3" />
                DERIVED
            </span>
        );
    }
    if (isProd) {
        return (
            <span data-testid={`source-badge-${entity}`} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-100 ${className}`}>
                <Sparkles className="w-3 h-3" />
                NATIVE DB / LIVE DATA
            </span>
        );
    }
    return (
        <span data-testid={`source-badge-${entity}`} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-slate-100 text-slate-600 border-slate-200 ${className}`}>
            <Database className="w-3 h-3" />
            DB / SEED DATA
        </span>
    );
}
